import React, { useEffect, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useQuery } from "react-query";
import { QueryKeys } from "@/network/queryKeys";
import { SafedealAPI } from "@/api/safedeal";
import { FORM_ID } from "../config";
import QueryRequest from "@/components/QueryRequest";
import { useSelector } from "react-redux";
import { FormContext, useForm, useFormContext } from "react-hook-form";
import { safedealPayoutTiBySbpSchema } from "@/validation/safe-deal";
import Empty from "@/components/Empty";
import isempty from "lodash/isEmpty";
import type { tRootForm, tBySbpForm } from "./types";
import { Button } from "_c/button";
import PhoneInput from "@/components/form/PhoneInput";
import { useLocale } from "@/hooks/useLocale";
import { AbstractControlledField } from "@/components/form/Field";
import TextInput from "_c/form/TextInput";
import BottomSheet from "@/components/BottomSheet";
import { useToggle } from "@/hooks/useToggle";
import { tTinkoffIndividualsSbpMember } from "@/types";
import { Selectors as UserSelectors } from "@/store/user/selectors";

type tProps = {
  t: TFunction;
  ns: string;
  onSubmit: (form: tRootForm) => any;
};

const BySbp = (props: tProps) => {
  const defaultPhone = useSelector(UserSelectors.getPhone);
  const methods = useForm<tBySbpForm>({
    defaultValues: {
      spb_member_id: undefined,
      spb_phone: defaultPhone || undefined,
    },
    validationSchema: safedealPayoutTiBySbpSchema,
  });
  const ns = props.ns + "by_sbp.";
  const onSubmit = (form: tBySbpForm) => props.onSubmit(form);
  const locale = useLocale();
  return (
    <>
      <FormContext {...methods}>
        <form id={FORM_ID} onSubmit={methods.handleSubmit(onSubmit)}>
          <AbstractControlledField
            name="sbp_phone"
            t={props.t}
            locale={locale}
            id="sbp_phone"
            inputComponent={TextInput}
            label={props.t(ns + "phone")}
            Component={PhoneInput}
          />
          <AbstractControlledField
            t={props.t}
            id="sbp_member"
            name="sbp_member_id"
            label={props.t(ns + "sbp_member")}
            Component={SbpBankField}
            ns={ns}
          />
        </form>
      </FormContext>
      <div
        css={`
          margin-top: 10px;
          text-align: center;
        `}
      ></div>
    </>
  );
};

const SbpBankField = ({
  ns,
  t,
}: {
  ns: string;
  value?: string;
  t: TFunction;
} & tAnyDict) => {
  const [isSbpListOpen, toggleSbpList] = useToggle();
  const [search, setSearch] = useState("");
  const { setValue, register } = useFormContext<tBySbpForm>();
  const [selectedMember, setSelectedMember_] = useState<
    tTinkoffIndividualsSbpMember | undefined
  >(undefined);
  const setSelectedMember = (member: tTinkoffIndividualsSbpMember) => {
    setValue("sbp_member_id", member.id);
    setSelectedMember_(member);
  };
  const membersQuery = useQuery(
    [QueryKeys.tinkoffIndividualsSbpMembers],
    () => SafedealAPI.tinkoffIndividuals.getSupportedSbpMembers(),
    {
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );
  const members = useMemo(() => {
    const members = membersQuery.data?.members;
    const normSearch = search.trim().toLowerCase();
    if (isempty(members)) return [];
    return members!.filter((m) => {
      const nameLower = m.name_ru?.toLowerCase() || "";
      return normSearch ? nameLower.includes(normSearch) : true;
    });
  }, [search, membersQuery.data]);
  useEffect(() => {
    // @ts-ignore
    register({ name: "sbp_member_id" });
  }, [register]);
  return (
    <>
      {selectedMember ? (
        <Button type="button" small greenish onClick={toggleSbpList}>
          {selectedMember.name_ru}
        </Button>
      ) : (
        <Button type="button" small primary onClick={toggleSbpList}>
          {t(ns + "open_sbp_member_picker")}
        </Button>
      )}
      <BottomSheet
        defaultHeight={window.innerHeight * 0.75}
        isOpen={isSbpListOpen}
        onClose={toggleSbpList}
      >
        <QueryRequest query={membersQuery}>
          <div
            css={`
              padding: 10px;
            `}
          >
            <TextInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("common:search")}
            />
            {isempty(members) ? (
              <Empty message={t("common:empty")} />
            ) : (
              <ul
                css={`
                  margin-top: 10px;
                `}
              >
                {members.map((m) => (
                  <li
                    role="button"
                    onClick={() => {
                      setSelectedMember(m);
                      toggleSbpList();
                    }}
                    css={`
                      cursor: pointer;
                      background: var(--bg-elevate);
                      margin-bottom: 7px;
                      padding: 10px;
                      border-radius: var(--root-br);
                    `}
                    key={m.id}
                  >
                    {m.name_ru}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </QueryRequest>
      </BottomSheet>
    </>
  );
};

export default BySbp;
